import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import type { Account } from '@limo/database'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useCreateOnboardingRun } from '@/hooks/useOnboarding'
import { ONBOARDING_TIMEZONES } from '@/lib/onboarding/steps'

const newRunSchema = z.object({
  account_id: z.string().min(1, 'Client account is required'),
  main_domain: z
    .string()
    .min(1, 'Main domain is required')
    .regex(
      /^(?!https?:\/\/)[a-z0-9.-]+\.[a-z]{2,}$/i,
      'Enter a bare domain like example.com (no https:// or paths)'
    ),
  ads_customer_id: z
    .string()
    .regex(/^(\d{3}-\d{3}-\d{4})?$/, 'Format: 123-456-7890')
    .optional()
    .or(z.literal('')),
  timezone: z.string().min(1, 'Timezone is required'),
})

type NewRunFormData = z.infer<typeof newRunSchema>

interface NewOnboardingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NewOnboardingDialog({
  open,
  onOpenChange,
}: NewOnboardingDialogProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const createRun = useCreateOnboardingRun()

  const { data: accounts } = useQuery({
    queryKey: ['accounts-select'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name')
        .eq('status', 'active')
        .order('name')
      if (error) throw error
      return data as Pick<Account, 'id' | 'name'>[]
    },
  })

  const form = useForm<NewRunFormData>({
    resolver: zodResolver(newRunSchema),
    defaultValues: {
      account_id: '',
      main_domain: '',
      ads_customer_id: '',
      timezone: 'America/New_York',
    },
  })

  const onSubmit = async (data: NewRunFormData) => {
    try {
      const runId = await createRun.mutateAsync({
        accountId: data.account_id,
        mainDomain: data.main_domain.toLowerCase(),
        adsCustomerId: data.ads_customer_id || null,
        timezone: data.timezone,
      })
      onOpenChange(false)
      form.reset()
      navigate(`/admin/onboarding/${runId}`)
    } catch (error) {
      toast({
        title: 'Failed to create onboarding run',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New onboarding</DialogTitle>
          <DialogDescription>
            Start a guided onboarding for a client account. This seeds the full
            step checklist; steps run from the wizard afterwards.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client account</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accounts?.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="main_domain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Main domain</FormLabel>
                  <FormControl>
                    <Input placeholder="acmelimo.com" {...field} />
                  </FormControl>
                  <FormDescription>
                    The client&apos;s root domain. Booking site goes on
                    book.{'{domain}'}, tracking on br.{'{domain}'}.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ads_customer_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Google Ads customer ID (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="123-456-7890" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Timezone</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ONBOARDING_TIMEZONES.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createRun.isPending}>
                {createRun.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Start onboarding
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
